import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Modal, Spinner } from 'react-bootstrap';
import TabellaLista from '../../../components/Tabella/TabellaLista';
import { ChipFilter, filtroMultiplo } from '../../../components/Tabella/ChipFilter';
import { LinkShareModal, prezzo, STATI_LINK, statoEffettivo } from '../../../components/LinkAcquisto/LinkAcquisto';
import MyApiClient from '../../../../api-client/MyApiClient';

/*
 * Link — i link d'acquisto generati dalla struttura nelle schede dei prodotti (corsi, abbonamenti,
 * lezioni, impianti): quando è stato creato, per quale prodotto, quante volte è stato aperto e
 * quante vendite ha portato e lo stato (la nota si vede nella finestra). Cliccando la riga si apre la finestra del link:
 * si ricondivide e si cambiano solo stato e nota.
 *
 * I dati vengono da GET /booking/purchaseLinks e si salvano con PATCH /booking/purchaseLinks/{token}.
 * Un prodotto può avere più link: ogni riga è un link, con le sue viste e vendite.
 */

const TIPI = { course: 'Corso', subscription: 'Abbonamento', lesson: 'Lezione', venue: 'Impianto' };
const TIPO_OPTIONS = Object.values(TIPI);
const STATO_OPTIONS = Object.values(STATI_LINK);

const data = (ms) => (ms ? new Date(ms).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '');

const riga = (l) => ({
    ...l,
    id: l.token,
    dataLabel: data(l.createdAt),
    titolo: l.title || 'Senza titolo',
    tipo: TIPI[l.productType] || l.productType || '',
    prodotto: [l.productName, l.productType === 'lesson' && l.coachName ? `con ${l.coachName}` : ''].filter(Boolean).join(' '),
    viste: Number(l.views) || 0,
    vendite: Number(l.sales) || 0,
    prezzo: typeof l.productPrice === 'number' ? l.productPrice : null,
    prezzoLabel: prezzo(l.productPrice),
    statoLabel: STATI_LINK[statoEffettivo(l)],
    nota: l.note || '',
});

export const LinkAcquistoListPage = () => {
    const [righe, setRighe] = useState(null);
    const [caricamento, setCaricamento] = useState(false);
    const [nonDisponibile, setNonDisponibile] = useState(false);
    const [statoFilter, setStatoFilter] = useState(STATO_OPTIONS);
    const [tipoFilter, setTipoFilter] = useState(TIPO_OPTIONS);
    const [aperto, setAperto] = useState(null);

    useEffect(() => {
        const carica = async () => {
            const dettagli = sessionStorage.getItem('userDetails');
            if (!dettagli) return;
            setCaricamento(true);
            const risposta = await new MyApiClient().getPurchaseLinks(JSON.parse(dettagli).idToken);
            setCaricamento(false);
            if (risposta?.error) {
                setNonDisponibile(true);
                setRighe([]);
                return;
            }
            setRighe(risposta.map(riga));
        };
        carica();
    }, []);

    const filtrate = useMemo(
        () => righe && righe.filter((r) => filtroMultiplo(statoFilter, STATO_OPTIONS, r.statoLabel) && filtroMultiplo(tipoFilter, TIPO_OPTIONS, r.tipo)),
        [righe, statoFilter, tipoFilter],
    );

    const colonne = [
        // Data, titolo e tipo non vanno mai a capo
        { Header: 'Data', Footer: 'Data', accessor: 'createdAt', compact: true, disableGlobalFilter: true, disableFilters: true, Cell: ({ row }) => row.original.dataLabel },
        {
            Header: 'Titolo', Footer: 'Titolo', accessor: 'titolo', larghezza: '30%', disableFilters: true,
            Cell: ({ value }) => <span style={{ whiteSpace: 'nowrap' }}>{value}</span>,
        },
        { Header: 'Tipo', Footer: 'Tipo', accessor: 'tipo', compact: true, disableFilters: true },
        { Header: 'Prodotto', Footer: 'Prodotto', accessor: 'prodotto', larghezza: '22%', disableFilters: true },
        {
            Header: 'Prezzo', Footer: 'Prezzo', accessor: 'prezzo', align: 'right', disableGlobalFilter: true, disableFilters: true,
            Cell: ({ row }) => row.original.prezzoLabel,
        },
        { Header: 'N. Viste', Footer: 'N. Viste', accessor: 'viste', align: 'center', disableGlobalFilter: true, disableFilters: true },
        { Header: 'N. Vendite', Footer: 'N. Vendite', accessor: 'vendite', align: 'center', disableGlobalFilter: true, disableFilters: true },
        {
            Header: 'Stato', Footer: 'Stato', accessor: 'statoLabel', disableGlobalFilter: true, disableFilters: true,
            Cell: ({ value }) => (
                <div className="d-flex align-items-center">
                    <i className={`fa fa-circle me-1 ${value === STATI_LINK.ACTIVE ? 'text-success' : 'text-warning'}`}></i>
                    {value}
                </div>
            ),
        },
    ];

    const salvato = (aggiornato) => {
        setRighe((vecchie) => vecchie.map((r) => (r.token === aggiornato.token ? riga({ ...r, ...aggiornato }) : r)));
        setAperto((a) => (a && a.token === aggiornato.token ? riga({ ...a, ...aggiornato }) : a));
    };

    return (
        <>
            <Modal className="fade" show={caricamento} centered>
                <Modal.Body className="text-center">
                    <Spinner animation="border" role="status"><span className="sr-only">Loading...</span></Spinner>
                    <div>Caricamento in corso...</div>
                </Modal.Body>
            </Modal>
            {nonDisponibile && (
                <Alert variant="warning" className="text-dark">
                    Non è stato possibile caricare l’elenco dei link. Riprova più tardi: intanto i link si creano e si
                    condividono dalle schede dei prodotti.
                </Alert>
            )}
            <div className="sid-link-page">
            <TabellaLista
                pageList={true}
                hasSearch={true}
                searchPlaceholder={'Cerca per titolo, prodotto o tipo'}
                hasFilter={false}
                filterControl={
                    <div className="sid-chip-filters sid-chip-filters--inline">
                        <ChipFilter label="Stato" options={STATO_OPTIONS} value={statoFilter} onChange={setStatoFilter} multiple />
                        <ChipFilter label="Tipo" options={TIPO_OPTIONS} value={tipoFilter} onChange={setTipoFilter} multiple />
                    </div>
                }
                data_column={colonne}
                data_json={filtrate}
                openColumn={'titolo'}
                openRow={(r) => setAperto(r)}
                sort_by={'createdAt'}
                sort_type={'desc'}
            />
            </div>
            <LinkShareModal show={!!aperto} onHide={() => setAperto(null)} link={aperto} nomeProdotto={aperto?.prodotto}
                acquistabile={aperto?.productPurchasable !== false} conStato onSalvato={salvato}
                onRimosso={(l) => setRighe((vecchie) => vecchie.filter((r) => r.token !== l.token))} />
        </>
    );
};

export default LinkAcquistoListPage;
